/**
 * /dashboard/hr/documents — Employee document store
 * (Phase 3c — 2026-06-01).
 *
 * SUBJECT SELECTION (whose document store is shown):
 *  - Default: the caller's OWN store. GET /hr/documents with no
 *    user_id resolves to the caller server-side; an employee only ever
 *    legitimately sees their own (employee-visibility) docs and the
 *    server filters that — there is NO client-side visibility logic.
 *  - HR admins additionally get an employee picker to view any
 *    employee's store (GET /hr/documents?user_id=N). The employee
 *    directory itself is HR-only (the picker 403s for non-HR and the
 *    self view is the only one offered then). A manager viewing a
 *    report's docs would also be authorized server-side, but the
 *    directory needed to PICK that report is HR-gated, so we honestly
 *    expose the picker only to HR and otherwise show "your documents".
 *
 * QA 2026-10 #5: the picker lists former employees too (flagged
 * "inactive") — a leaver's relieving letter, filed here when it was sent,
 * must stay reachable for the 8-year retention period — and the subject
 * is the ?user_id= search param, so the employee page's "Documents" link
 * opens that person's store directly.
 *
 * Upload targets the chosen subject; visibility is a server-enforced
 * security field. Delete is HR-admin-only (RBAC server-side; control
 * shown only to HR in DocumentList).
 *
 * Migration note (2026-06-01, Next.js → Vite): port of
 * frontend/src/app/(dashboard)/dashboard/hr/documents/page.tsx +
 * layout.tsx, flattened into one route that uses HrModuleShell. Swaps:
 *   - "use client" dropped
 */

import { createFileRoute, Navigate, useNavigate, useParams } from "@tanstack/react-router";
import { z } from "zod";
import { toApiError } from "@/lib/api";
import { documentsRedirectFor } from "@/lib/me-redirects";
import { useAuth } from "@/lib/auth";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { useEmployees } from "@/hooks/use-hr";
import { HrFeatureGate } from "@/components/hr/hr-feature-gate";
import { HrModuleShell } from "@/components/shared/hr-module-shell";
import { DocumentList } from "@/components/hr/document-list";
import { DocumentUploadDialog } from "@/components/hr/document-upload-dialog";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ?user_id= — whose store HR is viewing (the employee page links here).
const documentsSearchSchema = z.object({
  user_id: z.coerce.number().int().positive().optional().catch(undefined),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/hr/documents",
)({
  validateSearch: documentsSearchSchema,
  component: HrDocumentsRoute,
});

function HrDocumentsRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const { isHrAdmin } = useHrRoles();
  const selfId = user?.user_id;
  const navigate = useNavigate();

  // HR picks a subject (kept in the URL); everyone else is implicitly
  // their own store.
  const { user_id: subjectId } = Route.useSearch();
  const setSubjectId = (id: number) => {
    void navigate({
      to: "/app/$tenantSlug/hr/documents",
      params: { tenantSlug },
      search: id === selfId ? {} : { user_id: id },
      replace: true,
    });
  };
  const effectiveSubject = isHrAdmin
    ? (subjectId ?? selfId)
    : selfId;

  const employeesQ = useEmployees(isHrAdmin);
  const subjectIsSelf = effectiveSubject === selfId;
  const subject = employeesQ.data?.employees.find(
    (x) => x.id === effectiveSubject,
  );
  const subjectName = (() => {
    if (subjectIsSelf) return "your";
    return subject ? `${subject.first_name} ${subject.last_name}'s` : "this employee's";
  })();

  // Navigation rebuild (wave 3): anyone who is not an HR admin only ever
  // saw their own store here, under the HR tab strip (employee review
  // finding #1). Their home is Me → Documents now; old links land there.
  const meTwin = documentsRedirectFor(user?.role);
  if (meTwin) {
    return <Navigate to={meTwin} params={{ tenantSlug }} replace />;
  }

  return (
    <HrModuleShell
      title="Documents"
      description={
        isHrAdmin
          ? "Employee document store. Pick an employee, or manage your own."
          : "Your document store — contracts, payslips and more."
      }
      actions={
        effectiveSubject != null ? (
          <DocumentUploadDialog
            subjectUserId={effectiveSubject}
            subjectLabel={subjectName}
          />
        ) : undefined
      }
    >
      <HrFeatureGate>
        {isHrAdmin && (
          <Card className="mb-4">
            <CardContent className="flex flex-wrap items-center gap-3 py-4">
              <span className="text-sm font-medium text-gray-900">
                Viewing
              </span>
              <Select
                value={
                  effectiveSubject != null
                    ? String(effectiveSubject)
                    : undefined
                }
                onValueChange={(v) => setSubjectId(Number(v))}
              >
                <SelectTrigger aria-label="Employee" className="w-72">
                  <SelectValue placeholder="Select an employee" />
                </SelectTrigger>
                <SelectContent>
                  {selfId != null && (
                    <SelectItem value={String(selfId)}>
                      My Documents
                    </SelectItem>
                  )}
                  {employeesQ.isError && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      {toApiError(employeesQ.error).message}
                    </div>
                  )}
                  {(employeesQ.data?.employees ?? [])
                    .filter((e) => e.id !== selfId)
                    .sort((a, b) => {
                      // Current employees first, then former ones.
                      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
                      return `${a.first_name} ${a.last_name}`.localeCompare(
                        `${b.first_name} ${b.last_name}`,
                      );
                    })
                    .map((e) => (
                      <SelectItem key={e.id} value={String(e.id)}>
                        {e.first_name} {e.last_name}
                        {e.employee_id ? ` · ${e.employee_id}` : ""}
                        {e.is_active ? "" : " · Inactive"}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {subject && !subject.is_active && (
                <p className="text-xs text-muted-foreground" data-testid="documents-inactive-subject">
                  {subject.first_name} is no longer active. Their documents —
                  including letters sent when they left — stay here for the
                  retention period.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {effectiveSubject != null ? (
          <DocumentList subjectUserId={effectiveSubject} />
        ) : (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              Could not resolve your account. Try reloading.
            </CardContent>
          </Card>
        )}
      </HrFeatureGate>
    </HrModuleShell>
  );
}
